/**
 * Shared per-row busy registry (#1416, rowActions v2 capability 3).
 *
 * The pre-v2 `RowActionsCell` hid its in-flight state in a cell-local
 * `useRef`, so nothing outside the cell could observe whether a row's action
 * was running. rowActions v2 lifts that state into a registry ResourcePage
 * owns and shares through context, so the row-actions cell, the bulk bar, and
 * (later, #1417) the detail-panel footer all read ONE busy set for a row.
 *
 * The registry is a read-only subscription for consumers (`isBusy`) plus two
 * mutations the ACTION runner owns:
 *   - `begin(rowId)` — an ATOMIC claim: it returns `false` if the row is
 *     already busy, giving the synchronous double-activation guard the old
 *     ref provided (state updates are async, so a plain Set-in-state cannot).
 *   - `end(rowId)` — release the claim.
 *
 * Implementation: a `Set` in a ref (synchronous truth for the atomic claim)
 * plus a version bump that changes the CONTEXT VALUE, which is what re-renders
 * every consumer that read `isBusy` during render.
 *
 * The version has to live in the context value rather than rely on the owning
 * page re-rendering the tree (#1796): the table's rows are memoized now, so a
 * page-level re-render no longer reaches the row-actions cell — only a changed
 * context value does. The registry OBJECT still keeps one identity for the
 * page's lifetime, because `useRowActionRunner` keys its unmount cleanup on it
 * and a new identity per claim would re-run that cleanup and release the claim
 * mid-flight.
 */
import { createContext, useContext, useMemo, useReducer, useRef, type ReactNode } from 'react';

export interface RowBusyRegistry {
  /** Read-only subscription: is this row's action currently in flight? */
  isBusy: (rowId: string) => boolean;
  /**
   * Atomic claim. Returns `false` when the row is already busy (the
   * double-activation guard); `true` after claiming an idle row.
   */
  begin: (rowId: string) => boolean;
  /** Release a claim previously taken by `begin`. */
  end: (rowId: string) => void;
}

/**
 * What `useRowBusyRegistry` hands to `RowBusyProvider`: the stable registry
 * plus the claim counter that makes each claim/release visible to consumers.
 */
export interface RowBusyHandle {
  registry: RowBusyRegistry;
  /** Incremented on every claim and release. */
  version: number;
}

/**
 * Build a registry instance. Owned by ResourcePage (one per list) and passed
 * to `RowBusyProvider`. The registry keeps a stable identity across renders —
 * consumers call `isBusy()` each render rather than depending on the object.
 */
export function useRowBusyRegistry(): RowBusyHandle {
  const idsRef = useRef<Set<string>>(new Set());
  const [version, bump] = useReducer((n: number) => n + 1, 0);
  const registry = useMemo(
    () => ({
      isBusy: (rowId: string) => idsRef.current.has(rowId),
      begin: (rowId: string) => {
        if (idsRef.current.has(rowId)) return false;
        idsRef.current.add(rowId);
        bump();
        return true;
      },
      end: (rowId: string) => {
        if (idsRef.current.delete(rowId)) bump();
      },
    }),
    [],
  );
  return useMemo(() => ({ registry, version }), [registry, version]);
}

const RowBusyContext = createContext<RowBusyHandle | null>(null);

export function RowBusyProvider({
  handle,
  children,
}: {
  handle: RowBusyHandle;
  children: ReactNode;
}) {
  return <RowBusyContext.Provider value={handle}>{children}</RowBusyContext.Provider>;
}

/**
 * Subscribe to the busy set. The returned registry keeps ONE identity for the
 * provider's lifetime; the re-render on each claim/release comes from the
 * version travelling in the context value alongside it.
 */
export function useRowBusy(): RowBusyRegistry {
  const ctx = useContext(RowBusyContext);
  if (!ctx) {
    throw new Error('useRowBusy must be used within a RowBusyProvider');
  }
  return ctx.registry;
}
